import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import shopStore from '../stores/shopStore';
import logger from '../utils/logger';

const ShopRegistration = observer(({ open, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact_info: null
  });
  
  const [errors, setErrors] = useState({});

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Убираем ошибку при изменении поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Название магазина обязательно';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      logger.log('🏪 Отправка данных магазина:', formData);
      
      const shopData = {
        name: formData.name.trim(),
        address: formData.address.trim() || null,
        contact_info: formData.contact_info
      };

      await shopStore.createShop(shopData);
      
      logger.log('🏪 Магазин успешно зарегистрирован');
      
      // Сбрасываем форму
      setFormData({
        name: '',
        address: '',
        contact_info: null
      });
      setErrors({});
      
      // Вызываем callback успешной регистрации
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      logger.error('Ошибка при регистрации магазина:', error);
    }
  };

  const handleClose = () => {
    // Сбрасываем форму при закрытии
    setFormData({
      name: '',
      address: '',
      contact_info: null
    });
    setErrors({});
    shopStore.clearError();
    
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={shopStore.isLoading}
    >
      <DialogTitle>
        <Typography variant="h5" component="h2" gutterBottom>
          🏪 Регистрация магазина
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Для продолжения работы необходимо зарегистрировать ваш магазин
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          {shopStore.error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={shopStore.clearError}>
              {shopStore.error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Название магазина *"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={shopStore.isLoading}
            margin="normal"
            placeholder="Например: Магазин Сантехники №1"
          />

          <TextField
            fullWidth
            label="Адрес магазина"
            value={formData.address}
            onChange={handleInputChange('address')}
            error={!!errors.address}
            helperText={errors.address || 'Необязательное поле'}
            disabled={shopStore.isLoading}
            margin="normal"
            multiline
            rows={2}
            placeholder="Например: г. Москва, ул. Примерная, д. 123"
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              disabled={shopStore.isLoading}
              color="inherit"
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={shopStore.isLoading}
              startIcon={shopStore.isLoading ? <CircularProgress size={20} /> : null}
            >
              {shopStore.isLoading ? 'Регистрация...' : 'Зарегистрировать магазин'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
});

export default ShopRegistration; 